<template>
  <div ref="root" :class="{ 'is-touch': isTouchDevice }" class="rabbit">
    <div class="rabbit-inner wrapper">
      <div class="mt-d rabbit-content">
        <p
          :class="{ 'is-hidden': isHidden }"
          class="rabbit__headline p-medium"
          v-html="copy.intro.headline"
        ></p>
        <h1
          :class="{ 'is-hidden': isHidden }"
          class="rabbit__title h1-custom"
          v-html="copy.intro.title"
        ></h1>
        <span
          :class="{ 'is-hidden': isHidden }"
          class="rabbit__subtitle info-label"
          v-html="copy.intro.subtitle"
        ></span>

        <GCtaRounded
          v-if="isTouchDevice"
          text="Play the game"
          class="rabbit__cta"
          name="read-case"
          size="large"
          foreground="#332e2e"
          background="#7BEEFF"
          @click="onClick"
          :class="{ 'is-hidden': isHidden }"
        />
        <p
          v-if="!isTouchDevice"
          class="rabbit__text p-medium mb-d"
          v-html="copy.text"
        ></p>
        <p
          v-if="isPlayable && isTouchDevice"
          class="rabbit__tip p-medium"
          v-html="copy.touch"
        ></p>
      </div>
    </div>

    <canvas ref="canvas" class="canvas"></canvas>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable no-mixed-operators */
import { LoadingManager, RepeatWrapping, TextureLoader } from 'three'
import { ref, onMounted, reactive, onBeforeUnmount, onDeactivated } from 'vue'
import { Observer } from 'gsap/Observer'
import { gsap } from 'gsap'
import { ScrollToPlugin } from 'gsap/ScrollToPlugin'
import isMobile from 'ismobilejs'
import { useRoute } from 'vue-router'

import noise from '@/assets/textures/rabbit/noise.jpg?url'
import caustics from '@/assets/textures/rabbit/caustics.jpg?url'
import { Assets } from '@/components/rabbit/Assets'
import { World } from '@/components/rabbit/World'
import { useRaf, useResize } from '@/utils/aware'
import { logger } from '@/utils/logger'
import { useChromeStore } from '@/stores/chrome'

// gsap.registerPlugin(Observer)
gsap.registerPlugin(ScrollToPlugin)

const { t } = useChromeStore()
const route = useRoute()
const type = route.query.type || 'project'
const copy = reactive({
  intro: {
    headline: t('rabbit-intro-headline'),
    title: t('rabbit-intro-title'),
    subtitle: `${t('rabbit-intro-subtitle-start')}${
      type === 'project' ? '48' : '72'
    } ${t('rabbit-intro-subtitle-end')}`,
  },
  touch: t('rabbit-touch'),
  text: t('rabbit-text'),
})

let isActive = true
const isHidden = ref(false)
const isTouchDevice = ref(isMobile().any)
const isPlayable = ref(!isMobile().any)
const root = ref<HTMLElement>({} as HTMLElement)
const canvas = ref<HTMLCanvasElement>({} as HTMLCanvasElement)
const loadingManager = new LoadingManager()
const assets = new Assets()
const mousePos = { x: 0, y: 0, px: 0, py: 0, cx: 0, cy: 0 }

let world: World
let winWidth = 0
let winHeight = 0

const onClick = () => {
  document.documentElement.classList.add('no-scroll')
  isHidden.value = true
  isPlayable.value = true

  gsap.to(window, {
    scrollTo: {
      y: root.value,
      offsetY: 100,
      autoKill: false,
    },
    onComplete: () => {
      document.documentElement.style.height = `${window.innerHeight}px`
      // root.value.style.height = `${window.innerHeight}px`
      // onWindowResize()
    },
  })
}

const initLoading = () => {
  loadingManager.onLoad = () => {
    assetsLoadComplete()
  }

  loadingManager.onError = (url: string) => {
    logger.log(`There was an error loading ${url}`)
  }

  loadFiles()
}

const loadFiles = () => {
  const textureLoader = new TextureLoader(loadingManager)

  textureLoader.load(noise, texture => {
    // eslint-disable-next-line no-multi-assign
    texture.wrapS = texture.wrapT = RepeatWrapping
    assets.setTexture({ key: 'noise', texture })
  })

  textureLoader.load(caustics, texture => {
    // eslint-disable-next-line no-multi-assign
    texture.wrapS = texture.wrapT = RepeatWrapping
    assets.setTexture({ key: 'caustics', texture })
  })
}

const assetsLoadComplete = () => {
  setup()
  draw()
}

const onWindowResize = () => {
  winHeight = root.value?.offsetHeight
  winWidth = root.value?.offsetWidth
  if (world) {
    world.resize(winWidth, winHeight)
  }
}
const handleMouseMove = (e: Observer) => {
  if (!isPlayable.value || !canvas.value) {
    return
  }

  const bcr = canvas.value.getBoundingClientRect()
  const x = e.x || 0
  const y = e.y || 0

  mousePos.x = x - bcr.left
  mousePos.y = y - bcr.top
  mousePos.px = (mousePos.x / winWidth) * 2 - 1
  mousePos.py = (mousePos.y / winHeight) * 2 - 1
  mousePos.cx = mousePos.x - winWidth / 2
  mousePos.cy = mousePos.y - winHeight / 2

  if (world) {
    world.mouseMove(mousePos)
  }
}
const handleMouseDown = () => {
  if (!isPlayable.value) {
    return
  }

  if (world) {
    world.mouseDown()
  }
}

const setup = () => {
  const background = getComputedStyle(
    document.documentElement
  ).getPropertyValue('--c-background')

  world = new World({
    height: root.value?.offsetHeight,
    width: root.value?.offsetWidth,
    canvas: canvas.value,
    assets,
    background,
  })
  Observer.create({
    type: 'touch, pointer',
    target: document.body,
    onMove: handleMouseMove,
    onPress: handleMouseDown,
  })
  useResize(onWindowResize)
  onWindowResize()
  useRaf(() => {
    draw()
  })
}

const draw = () => {
  if (!isActive) {
    return
  }

  if (world) {
    world.render()
  }
}

let obs: MutationObserver
const initObserver = () => {
  obs = new MutationObserver(mutations => {
    const currentValue = (
      mutations[0].target as HTMLElement
    ).style.getPropertyValue('--c-background')
    world && world.updateFloorColor(currentValue)
  })

  obs.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style'],
  })
}

onMounted(() => {
  initLoading()
  initObserver()
})

onBeforeUnmount(() => {
  isActive = false
  world && world.destroy()
  document.documentElement.style.height = 'auto'
  document.documentElement.classList.remove('no-scroll')
})

onDeactivated(() => {
  obs && obs.disconnect()
})
</script>

<style lang="scss">
.footer {
  .no-scroll & {
    display: none;
  }
}
</style>

<style lang="scss" scoped>
$bp: m;

.is-hidden {
  opacity: 0;
  transition: opacity 0.25s;
}

.rabbit {
  position: relative;
  z-index: 10;
}

.rabbit-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  color: var(--c-foreground);
  text-align: center;
  transition: opacity 0.25s;
  user-select: none;

  @include mq(xxl) {
    min-height: calc(100vh - #{$header-height});
  }
}

.rabbit__headline {
  // prettier-ignore
  @include fluid(margin-bottom,(xxs: 5px, xxl: 30px));
}

.rabbit__subtitle {
  margin-bottom: 5rem;

  :deep(b),
  :deep(strong) {
    display: block;
  }
}

.rabbit__cta {
  margin-top: 7rem;
  margin-bottom: 5rem;
}

.rabbit__text {
  // prettier-ignore
  @include fluid(margin-top,(xxs: 150px, xxl: 350px));

  display: block;

  .is-touch & {
    display: none;
  }

  :deep(small) {
    display: block;
    margin-top: 2rem;
    font-size: 1.2rem;
    line-height: 1.2;
  }
}

.canvas {
  @include get-all-space;

  top: 3rem;
  user-select: none;

  @include mq($bp) {
    top: 8rem;
  }
}

.rabbit__tip {
  position: absolute;
  z-index: 10;
  bottom: -3rem;
  left: 0;
  width: 100%;
  padding: 2rem;
  background-color: var(--c-background);
  border-top: 1px solid var(--c-foreground);
}
</style>
